// @givmo/board — populating a form from the consent/resolution library, client-side.
//
// ══ WHY THE FILLING HAPPENS HERE AND NOT ON THE BACKEND ═════════════════════════════════════════
//
// The library is Band 2 under plan §3.2, and its four rails are what keep it there: organization-SELECTED
// (two read routes, no code path from an organization's facts to a form), organization-POPULATED (every operative
// term is a `[bracketed]` field), provenance-labelled, and labelled for counsel. The second rail is the
// one this module serves. The backend hands back the form's text and the list of its bracketed fields
// and then stops — it has nothing to say about what any of them should contain, and there is no
// endpoint that would let it acquire an opinion. The officer types the terms; what reaches
// `POST …/written-consents` is a body the organization composed.
//
// So this file is a text substitution and deliberately nothing more. It has no defaults, no
// suggestions, no derivation from the organization's own record, and no field it can populate on its own.
//
// ══ THE TRAP: A BRACKETED FIELD CAN BE WRAPPED ACROSS LINES ═════════════════════════════════════
//
// The forms are laid out for reading, so a long field is broken over two lines with indentation:
//
//     [Name of an office the bylaws
//       or the board establishes]      [Name of the person elected to it]
//
// while `fill_in_fields` carries it as one flat string, `"[Name of an office the bylaws or the board
// establishes]"`. A literal `replaceAll` therefore matches NOTHING for that field — the officer types a
// value, the preview does not change, and the form goes out with a bracket still in it. Matching is
// whitespace-flexible for exactly this reason: the inner text is escaped, then every run of whitespace
// becomes `\s+`, so one field matches whichever way the form happens to wrap it.
//
// An UNFILLED field is left exactly as it is — bracket and all — never blanked. A blank is invisible;
// a bracket is what tells an officer proof-reading the text that a term is still missing, and
// `remainingFields` is what lets the UI say so before they circulate it to the board.

/** A field's exact name as `fill_in_fields` gives it — brackets included. */
export type TemplateFieldName = string;

/** What the officer typed, keyed by the field's exact name. */
export type TemplateFieldValues = Readonly<Record<TemplateFieldName, string>>;

/** Escape a literal for use inside a `RegExp`. */
function escapeForRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * A pattern matching `field` however the form wraps it.
 *
 * Built from the field's INNER text so the brackets are anchored literally, with every internal
 * whitespace run relaxed to `\s+`. Returns `null` for anything that is not a `[…]` field, so a
 * malformed entry is skipped rather than turned into a pattern that matches half the document.
 */
export function templateFieldPattern(field: TemplateFieldName): RegExp | null {
  const trimmed = field.trim();
  if (!trimmed.startsWith('[') || !trimmed.endsWith(']') || trimmed.length < 3) return null;
  const inner = trimmed.slice(1, -1).trim();
  if (inner.length === 0) return null;
  const flexible = inner.split(/\s+/).map(escapeForRegex).join('\\s+');
  return new RegExp(`\\[\\s*${flexible}\\s*\\]`, 'g');
}

/**
 * Substitute every field the officer has filled in, leaving the rest as brackets.
 *
 * Replacement is done with a FUNCTION rather than a string, so a value containing `$&` or `$1` is
 * inserted as typed instead of being read as a back-reference — a real hazard when the value is a
 * board's own resolution text.
 */
export function fillTemplateBody(
  body: string,
  fields: readonly TemplateFieldName[],
  values: TemplateFieldValues,
): string {
  let filled = body;
  for (const field of fields) {
    const value = (values[field] ?? '').trim();
    if (value.length === 0) continue;
    const pattern = templateFieldPattern(field);
    if (pattern === null) continue;
    filled = filled.replace(pattern, () => value);
  }
  return filled;
}

/**
 * The fields still standing unfilled in `body` — what the officer has left to do.
 *
 * Read off the TEXT rather than off the values map, so a field the officer filled and then cleared, or
 * one they deleted from the body outright (several forms invite exactly that — "or delete this
 * paragraph"), answers correctly.
 */
export function remainingFields(
  body: string,
  fields: readonly TemplateFieldName[],
): TemplateFieldName[] {
  return fields.filter((field) => {
    const pattern = templateFieldPattern(field);
    return pattern !== null && pattern.test(body);
  });
}

/** A field name rendered as a form label — the brackets are markup, not part of the question. */
export function templateFieldLabel(field: TemplateFieldName): string {
  const trimmed = field.trim();
  const inner = trimmed.startsWith('[') && trimmed.endsWith(']') ? trimmed.slice(1, -1) : trimmed;
  return inner.replace(/\s+/g, ' ').trim();
}

/**
 * Whether a field wants a multi-line answer.
 *
 * A heuristic about INPUT SHAPE and nothing else: the forms' longer fields are written as instructions
 * ("State what the board is deciding, in the Organization's own words"), and giving those a one-line
 * input is how an officer ends up writing a resolution into a box that shows eight words of it. It
 * changes which control renders; it never changes what is sent.
 */
export function isLongFormField(field: TemplateFieldName): boolean {
  const label = templateFieldLabel(field);
  return /^(state|add|describe|list)\b/i.test(label) || label.length > 60;
}
